import type { ElementType, HTMLAttributeAnchorTarget, ReactNode } from "react";
import { Link } from "react-router-dom";
import ExternalLinkIcon from "@/components/ui/OpticksIcons/ExternalLinkIcon";
import MailIcon from "@/components/ui/OpticksIcons/MailIcon";

export interface ResolveLinkTargetArgs {
  to?: string;
  href?: string;
  target?: HTMLAttributeAnchorTarget;
  rel?: string;
  disabled?: boolean;
}

interface LinkElementProps {
  to?: string;
  href?: string;
  target?: HTMLAttributeAnchorTarget;
  rel?: string;
  "aria-disabled"?: boolean;
}

export interface ResolvedLinkTarget {
  /** Element to render: react-router `<Link>` for internal routes, else `<a>`. */
  Element: ElementType;
  /** Destination props to spread onto `Element` (element-specific). */
  targetProps: LinkElementProps;
  isInternal: boolean;
  isExternal: boolean;
  isMailto: boolean;
  /** Auto leading affordance icon (mail for `mailto:`). Combine with any explicit
   *  icon as `icon ?? leadingIcon` so a passed icon wins. */
  leadingIcon: ReactNode;
  /** Auto trailing affordance icon (external-link for a new-tab external href). */
  trailingIcon: ReactNode;
}

/**
 * Single source of truth for link semantics *and* affordances, shared by
 * OpticksLink and OpticksButton. Given a destination it decides which element to
 * render, the destination/`rel`/`aria-disabled` props for it, and the affordance
 * icons — but stays headless about layout: callers own className/children and
 * apply their own icon precedence (`icon ?? leadingIcon`).
 *
 * The chosen prop encodes intent, so nothing else needs restating:
 * - `to` (internal route) → react-router `<Link>` (SPA nav). Falls back to `<a>`
 *   when disabled, since anchors can't be natively disabled.
 * - `href` = leaving the app, so any non-`mailto:` href is **external** and
 *   always opens a new tab: `target="_blank"` + `rel="noopener noreferrer"` +
 *   trailing external icon.
 * - `href="mailto:…"` → `<a>`, no target, leading mail icon.
 * `target` is an optional override for the rare same-tab external (`"_self"`).
 */
export function resolveLinkTarget({
  to,
  href,
  target,
  rel,
  disabled = false,
}: ResolveLinkTargetArgs): ResolvedLinkTarget {
  const isInternal = to != null;
  const isMailto = !!href && href.startsWith("mailto:");
  const isExternal = !isInternal && !isMailto && href != null;
  const resolvedTarget = target ?? (isExternal ? "_blank" : undefined);
  const resolvedRel = isExternal ? (rel ?? "noopener noreferrer") : rel;

  const leadingIcon = isMailto ? <MailIcon /> : undefined;
  const trailingIcon = isExternal ? <ExternalLinkIcon /> : undefined;

  if (isInternal && !disabled) {
    return {
      Element: Link,
      targetProps: { to, target },
      isInternal,
      isExternal,
      isMailto,
      leadingIcon,
      trailingIcon,
    };
  }

  return {
    Element: "a",
    targetProps: {
      href: disabled ? undefined : (href ?? to),
      target: resolvedTarget,
      rel: resolvedRel,
      "aria-disabled": disabled || undefined,
    },
    isInternal,
    isExternal,
    isMailto,
    leadingIcon,
    trailingIcon,
  };
}
